import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  useColorModeValue,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import { Shield, FileText, Clock, Users, Brain, Activity } from 'lucide-react';

const PolicyPanel = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const policyMetrics = [
    {
      title: "Active Policies",
      value: "42",
      category: "Total",
      distribution: [
        { name: "Clinical", value: 15 },
        { name: "Technical", value: 12 },
        { name: "Operational", value: 8 },
        { name: "Regulatory", value: 7 }
      ]
    },
    {
      title: "Review Status",
      value: "92%",
      category: "Up to Date",
      distribution: [
        { name: "Current", value: 38 },
        { name: "Due Soon", value: 3 },
        { name: "Overdue", value: 1 }
      ]
    },
    {
      title: "Integration Coverage",
      value: "100%",
      category: "Systems",
      distribution: [
        { name: "Neural Lab", value: 12 },
        { name: "Content", value: 15 },
        { name: "Crowdsourcing", value: 15 }
      ]
    }
  ];

  const recentPolicies = [
    {
      name: "Clinical Data Governance",
      type: "Clinical",
      status: "Active",
      lastReview: "2025-03-15",
      nextReview: "2025-06-15",
      coverage: 100,
      owner: "Ethics Committee"
    },
    {
      name: "AI Model Validation",
      type: "Technical",
      status: "Under Review",
      lastReview: "2025-02-28",
      nextReview: "2025-03-28",
      coverage: 92,
      owner: "Data Science Team"
    },
    {
      name: "Content Adaptation Rules",
      type: "Operational",
      status: "Active",
      lastReview: "2025-03-01",
      nextReview: "2025-06-01",
      coverage: 98,
      owner: "Content Team"
    }
  ];

  const systemIntegration = [
    {
      system: "Neural Lab",
      policies: 12,
      compliance: 100,
      status: "Compliant"
    },
    {
      system: "Content Pipeline",
      policies: 15,
      compliance: 98,
      status: "Compliant"
    },
    {
      system: "Crowdsourcing",
      policies: 15,
      compliance: 96,
      status: "Review Required"
    }
  ];

  return (
    <VStack spacing={6} align="stretch">
      {/* Policy Metrics */}
      <SimpleGrid columns={3} spacing={4}>
        {policyMetrics.map((metric, index) => (
          <Box
            key={index}
            bg={bgColor}
            p={4}
            borderRadius="md"
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <VStack align="start" spacing={3}>
              <HStack>
                <Icon
                  as={index === 0 ? FileText : index === 1 ? Clock : Shield}
                  color="methodist.blue"
                  boxSize={5}
                />
                <Text fontSize="sm" fontWeight="medium">{metric.title}</Text>
              </HStack>
              <Stat>
                <StatNumber fontSize="2xl" color="methodist.blue">
                  {metric.value}
                </StatNumber>
                <StatLabel fontSize="xs" color="gray.500">
                  {metric.category}
                </StatLabel>
              </Stat>
              <Box width="100%">
                <SimpleGrid columns={2} spacing={2}>
                  {metric.distribution.map((item, idx) => (
                    <HStack key={idx} justify="space-between">
                      <Text fontSize="xs" color="gray.500">{item.name}</Text>
                      <Text fontSize="xs" fontWeight="medium">{item.value}</Text>
                    </HStack>
                  ))}
                </SimpleGrid>
              </Box>
            </VStack>
          </Box>
        ))}
      </SimpleGrid>

      {/* Recent Policies */}
      <Box
        bg={bgColor}
        p={4}
        borderRadius="md"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <Text fontSize="lg" fontWeight="medium" mb={4}>Recent Policies</Text>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Policy</Th>
              <Th>Type</Th>
              <Th>Status</Th>
              <Th>Review Dates</Th>
              <Th>Coverage</Th>
              <Th>Owner</Th>
            </Tr>
          </Thead>
          <Tbody>
            {recentPolicies.map((policy, index) => (
              <Tr key={index}>
                <Td>
                  <Text fontWeight="medium">{policy.name}</Text>
                </Td>
                <Td>
                  <Badge colorScheme="purple">{policy.type}</Badge>
                </Td>
                <Td>
                  <Badge
                    colorScheme={
                      policy.status === 'Active' ? 'green' :
                      policy.status === 'Under Review' ? 'orange' :
                      'red'
                    }
                  >
                    {policy.status}
                  </Badge>
                </Td>
                <Td>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color="gray.500">Last: {policy.lastReview}</Text>
                    <Text fontSize="xs" color="gray.500">Next: {policy.nextReview}</Text>
                  </VStack>
                </Td>
                <Td>
                  <HStack spacing={2}>
                    <Progress
                      value={policy.coverage}
                      size="sm"
                      width="60px"
                      colorScheme={policy.coverage >= 98 ? 'green' : 'blue'}
                    />
                    <Text fontSize="xs">{policy.coverage}%</Text>
                  </HStack>
                </Td>
                <Td>
                  <Text fontSize="sm">{policy.owner}</Text>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* System Integration */}
      <SimpleGrid columns={3} spacing={4}>
        {systemIntegration.map((system, index) => (
          <Box
            key={index}
            bg={bgColor}
            p={4}
            borderRadius="md"
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <VStack align="start" spacing={3}>
              <HStack justify="space-between" width="100%">
                <HStack>
                  <Icon
                    as={index === 0 ? Brain : index === 1 ? Activity : Users}
                    color="methodist.blue"
                    boxSize={4}
                  />
                  <Text fontSize="sm" fontWeight="medium">{system.system}</Text>
                </HStack>
                <Badge
                  colorScheme={
                    system.status === 'Compliant' ? 'green' : 'orange'
                  }
                >
                  {system.status}
                </Badge>
              </HStack>
              <SimpleGrid columns={2} spacing={4} width="100%">
                <VStack align="start" spacing={0}>
                  <Text fontSize="xs" color="gray.500">Policies</Text>
                  <Text fontSize="sm" fontWeight="medium">{system.policies}</Text>
                </VStack>
                <VStack align="start" spacing={0}>
                  <Text fontSize="xs" color="gray.500">Compliance</Text>
                  <Text fontSize="sm" fontWeight="medium">{system.compliance}%</Text>
                </VStack>
              </SimpleGrid>
              <Progress
                value={system.compliance}
                size="sm"
                width="100%"
                colorScheme={system.compliance >= 98 ? 'green' : 'blue'}
              />
            </VStack>
          </Box>
        ))}
      </SimpleGrid>
    </VStack>
  );
};

export default PolicyPanel;
